import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import tenant from '../../config/tenant';
import { getEmployeeProfilePicUrl } from '../../utils/employeeUtils';
import { fetchAuthedBlobUrl } from '../../utils/authedFile';

const IMAGE_BASE = tenant.api.employeeImageBaseUrl;

export default function EmployeeAvatar({ serviceNo, employee, directUrl: customDirectUrl, initials = '?', size = 44, className = '' }) {
  const images = useSelector((s) => s.images?.images || {});
  const empSvcNo = serviceNo || employee?.HED_SERVICE_NO || employee?.p_ced_service_no || employee?.HED_EMPLOYEE_ID;
  const uploadedImage = empSvcNo ? images[String(empSvcNo).trim()] : null;
  const directUrl = customDirectUrl || getEmployeeProfilePicUrl(employee);

  const [apiImgSrc, setApiImgSrc] = useState(null);
  const [apiLoading, setApiLoading] = useState(false);
  const [apiFailed, setApiFailed] = useState(false);

  /* `new Image()` and `<img src>` cannot carry the auth-key header, so the
     picture endpoint answered 401 and every avatar fell back to initials. The
     file is fetched with the header instead and shown from a blob URL. */
  useEffect(() => {
    let cancelled = false;
    if (uploadedImage) { setApiFailed(false); setApiLoading(false); return undefined; }

    const target = directUrl || (empSvcNo ? `${IMAGE_BASE}?serviceNo=${encodeURIComponent(empSvcNo)}` : null);
    if (!target) { setApiFailed(true); setApiLoading(false); return undefined; }

    setApiLoading(true);
    setApiFailed(false);

    fetchAuthedBlobUrl(target)
      .then((src) => { if (!cancelled) { setApiImgSrc(src); setApiLoading(false); } })
      .catch(() => { if (!cancelled) { setApiFailed(true); setApiLoading(false); } });

    return () => { cancelled = true; };
  }, [directUrl, serviceNo, uploadedImage]);

  const style = { width: size, height: size };
  const base = `rounded-xl object-cover shrink-0 ring-2 ring-white shadow-md ${className}`;

  if (uploadedImage) return <img src={uploadedImage} alt={initials} style={style} className={base} />;

  if (apiLoading) {
    return (
      <div style={style} className={`rounded-xl shrink-0 bg-white/10 flex items-center justify-center ${className}`}>
        <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
      </div>
    );
  }

  if (!apiFailed && apiImgSrc) return <img src={apiImgSrc} alt={initials} style={style} className={base} />;

  return (
    <div style={style} className={`rounded-xl bg-white/15 flex items-center justify-center shrink-0 shadow-md ring-2 ring-white/30 ${className}`}>
      <span className="text-white font-bold" style={{ fontSize: size * 0.36 }}>{initials}</span>
    </div>
  );
}